<template>
  <main class="news-feed-main">
    <div class="feed-container">
      <!-- Laddar skeleton (initial laddning) -->
      <div v-if="isLoading && items.length === 0" class="skeleton-list">
        <div v-for="n in 8" :key="n" class="skeleton-item" :class="{ 'compact-mode': isCompact }">
          <div class="skeleton-meta">
            <span class="skeleton-pill"></span>
            <span class="skeleton-time"></span>
          </div>
          <div class="skeleton-title"></div>
          <div class="skeleton-title short"></div>
        </div>
      </div>

      <!-- Felmeddelande -->
      <div v-else-if="errorMessage && items.length === 0" class="error-container">
        <div class="error-icon">⚠️</div>
        <h3>{{ t('errorTitle') }}</h3>
        <p>{{ errorMessage }}</p>
        <button type="button" class="retry-btn" @click="$emit('retry')">
          {{ t('retry') }}
        </button>
      </div>

      <!-- Inga artiklar hittades (sök/filter) -->
      <div v-else-if="items.length === 0" class="empty-container">
        <p class="empty-text">{{ t('emptyTitle') }}</p>
        <button 
          v-if="hasActiveFilter" 
          type="button" 
          class="reset-btn"
          @click="$emit('reset-filters')"
        >
          {{ t('resetFilters') }}
        </button>
      </div>

      <!-- Nyhetsflöde -->
      <div v-else class="feed-list">
        <!-- Grupp: Senaste timmen -->
        <section v-if="recentItems.length > 0" class="time-group">
          <header class="group-header">
            <span class="group-title">{{ t('lastHour') }}</span>
            <span class="group-count">{{ recentItems.length }}</span>
          </header>
          <div class="group-items">
            <NewsItem 
              v-for="item in recentItems" 
              :key="item.id" 
              :item="item" 
              :is-compact="isCompact"
            />
          </div>
        </section>

        <!-- Grupp: Tidigare idag -->
        <section v-if="todayItems.length > 0" class="time-group">
          <header class="group-header">
            <span class="group-title">{{ t('earlierToday') }}</span>
            <span class="group-count">{{ todayItems.length }}</span>
          </header>
          <div class="group-items">
            <NewsItem 
              v-for="item in todayItems" 
              :key="item.id" 
              :item="item" 
              :is-compact="isCompact"
            />
          </div>
        </section>

        <!-- Grupp: Äldre -->
        <section v-if="olderItems.length > 0" class="time-group">
          <header class="group-header">
            <span class="group-title">{{ t('earlierNews') }}</span>
            <span class="group-count">{{ olderItems.length }}</span>
          </header>
          <div class="group-items">
            <NewsItem 
              v-for="item in olderItems" 
              :key="item.id" 
              :item="item" 
              :is-compact="isCompact"
            />
          </div>
        </section>

        <!-- Sentinel element för IntersectionObserver infinite scroll -->
        <div ref="scrollSentinel" class="scroll-sentinel"></div>

        <!-- Oändlig scroll / Ladda mer sektion -->
        <div v-if="hasMore" class="load-more-section">
          <div class="load-spinner"></div>
          <span class="load-more-info">
            {{ t('showingCount', { visible: items.length, total: totalCount }) }}
          </span>
          <button 
            type="button" 
            class="manual-load-btn" 
            @click="$emit('load-more')"
          >
            {{ t('loadMore') }}
          </button>
        </div>

        <div v-else-if="items.length > 0" class="end-of-feed">
          <span class="end-dot">✓</span>
          <span>{{ t('readAll', { total: totalCount }) }}</span>
        </div>
      </div>

      <!-- Fotnot med Omni-stil info -->
      <footer v-if="items.length > 0" class="feed-footer">
        <p>{{ t('footerNote') }}</p>
      </footer>
    </div>
  </main>
</template>

<script>
import NewsItem from './NewsItem.vue'
import { t } from '../services/i18n'

export default {
  name: 'NewsFeed',
  components: {
    NewsItem
  },
  props: {
    lang: {
      type: String,
      default: 'en'
    },
    items: {
      type: Array,
      default: () => []
    },
    totalCount: {
      type: Number,
      default: 0
    },
    hasMore: {
      type: Boolean,
      default: false
    },
    isLoading: {
      type: Boolean,
      default: false
    },
    errorMessage: {
      type: String,
      default: ''
    },
    isCompact: {
      type: Boolean,
      default: false
    },
    hasActiveFilter: {
      type: Boolean,
      default: false
    }
  },
  emits: ['retry', 'reset-filters', 'load-more'],
  data() {
    return {
      observer: null
    }
  },
  computed: {
    recentItems() {
      const oneHourAgo = Date.now() - (60 * 60 * 1000)
      return this.items.filter(item => {
        const time = new Date(item.pubDate).getTime()
        return !isNaN(time) && time >= oneHourAgo
      })
    },
    todayItems() {
      const oneHourAgo = Date.now() - (60 * 60 * 1000)
      const twentyFourHoursAgo = Date.now() - (24 * 60 * 60 * 1000)
      return this.items.filter(item => {
        const time = new Date(item.pubDate).getTime()
        return !isNaN(time) && time < oneHourAgo && time >= twentyFourHoursAgo
      })
    },
    olderItems() {
      const twentyFourHoursAgo = Date.now() - (24 * 60 * 60 * 1000)
      return this.items.filter(item => {
        const time = new Date(item.pubDate).getTime()
        return isNaN(time) || time < twentyFourHoursAgo
      })
    }
  },
  mounted() {
    this.setupObserver()
  },
  updated() {
    if (!this.observer) {
      this.setupObserver()
    }
  },
  beforeUnmount() {
    if (this.observer) {
      this.observer.disconnect()
      this.observer = null
    }
  },
  methods: {
    t(key, params) {
      return t(this.lang, key, params)
    },
    setupObserver() {
      if (typeof window === 'undefined' || !('IntersectionObserver' in window)) return
      if (!this.$refs.scrollSentinel) return

      if (this.observer) {
        this.observer.disconnect()
      }

      this.observer = new IntersectionObserver(
        (entries) => {
          const entry = entries[0]
          if (entry && entry.isIntersecting && this.hasMore && !this.isLoading) {
            this.$emit('load-more')
          }
        },
        {
          rootMargin: '250px 0px'
        }
      )

      this.observer.observe(this.$refs.scrollSentinel)
    }
  }
}
</script>

<style scoped>
.news-feed-main {
  min-height: 80vh;
  padding: 16px 12px 60px;
}

.feed-container {
  max-width: 900px;
  margin: 0 auto;
  background-color: var(--card-bg);
  border-radius: 8px;
  border: 1px solid var(--border-color);
  box-shadow: var(--card-shadow);
  overflow: hidden;
}

.time-group {
  margin-bottom: 0;
}

.group-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: var(--group-header-bg);
  padding: 8px 20px;
  border-top: 1px solid var(--border-color);
  border-bottom: 1px solid var(--border-color);
}

.time-group:first-child .group-header {
  border-top: none;
}

.group-title {
  font-size: 0.76rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--text-muted);
}

.group-count {
  font-size: 0.74rem;
  font-weight: 700;
  color: var(--text-muted);
  background-color: var(--badge-count-bg);
  padding: 1px 7px;
  border-radius: 10px;
}

/* Skeleton loader */
.skeleton-list {
  padding: 0;
}

.skeleton-item {
  padding: 18px 20px;
  border-bottom: 1px solid var(--border-color);
}

.skeleton-item.compact-mode {
  padding: 12px 16px;
}

.skeleton-meta {
  display: flex;
  gap: 10px;
  margin-bottom: 12px;
}

.skeleton-pill {
  width: 70px;
  height: 18px;
  background-color: var(--skeleton-bg);
  border-radius: 3px;
  animation: shimmer 1.5s infinite ease-in-out;
}

.skeleton-time {
  width: 50px;
  height: 18px;
  background-color: var(--skeleton-bg);
  border-radius: 3px;
  animation: shimmer 1.5s infinite ease-in-out;
}

.skeleton-title {
  height: 18px;
  background-color: var(--skeleton-bg);
  border-radius: 4px;
  margin-bottom: 8px;
  animation: shimmer 1.5s infinite ease-in-out;
}

.skeleton-title.short {
  width: 60%;
  margin-bottom: 0;
}

@keyframes shimmer {
  0% { opacity: 0.5; }
  50% { opacity: 1; }
  100% { opacity: 0.5; }
}

/* Felmeddelande och tomt tillstånd */
.error-container, .empty-container {
  padding: 50px 20px;
  text-align: center;
}

.error-icon {
  font-size: 2.2rem;
  margin-bottom: 12px;
}

.error-container h3 {
  margin: 0 0 8px;
  font-size: 1.2rem;
  color: var(--text-primary);
}

.error-container p, .empty-text {
  color: var(--text-muted);
  margin-bottom: 20px;
  font-size: 0.95rem;
}

.retry-btn, .reset-btn {
  background-color: var(--accent-color);
  color: #ffffff;
  border: none;
  padding: 10px 20px;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.15s ease;
}

.retry-btn:hover, .reset-btn:hover {
  opacity: 0.9;
}

/* Infinite scroll & load more */
.scroll-sentinel {
  height: 20px;
  width: 100%;
  pointer-events: none;
}

.load-more-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 24px 20px;
  border-top: 1px solid var(--border-color);
  background-color: var(--group-header-bg);
}

.load-spinner {
  width: 22px;
  height: 22px;
  border: 2px solid var(--border-color);
  border-top-color: var(--accent-color);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  100% { transform: rotate(360deg); }
}

.load-more-info {
  font-size: 0.82rem;
  color: var(--text-muted);
  font-weight: 500;
}

.manual-load-btn {
  background-color: var(--card-bg);
  border: 1px solid var(--border-color);
  color: var(--text-primary);
  font-size: 0.82rem;
  font-weight: 600;
  padding: 6px 16px;
  border-radius: 20px;
  cursor: pointer;
  transition: all 0.15s ease;
}

.manual-load-btn:hover {
  background-color: var(--pill-hover-bg);
}

.end-of-feed {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 20px;
  font-size: 0.84rem;
  color: var(--text-muted);
  border-top: 1px solid var(--border-color);
  background-color: var(--group-header-bg);
}

.end-dot {
  color: #10b981;
  font-weight: bold;
}

.feed-footer {
  padding: 16px 20px;
  background-color: var(--card-bg);
  border-top: 1px solid var(--border-color);
  text-align: center;
}

.feed-footer p {
  margin: 0;
  font-size: 0.8rem;
  color: var(--text-muted);
}
</style>
